/**
 * The auto-refresh poller's view of renames applied optimistically but not yet
 * confirmed by Drive.
 *
 * WHY THIS EXISTS
 * FileExplorer paints a rename immediately, then awaits the Drive API. The 5s
 * folder poller can fetch inside that window and get the OLD name back, because
 * mergeDriveChildren takes `name` from the server unconditionally — so the row
 * visibly snaps back until Drive propagates. The same race was already solved for
 * deletes via pendingDriveDeleteIdsRef; this is the rename counterpart.
 *
 * WHY OVERRIDE RATHER THAN FILTER
 * A pending delete wants the row gone, so filtering it out is right. A pending
 * rename wants the row present, carrying our name.
 */

/**
 * Substitute pending names into a freshly fetched item list.
 *
 * Not pure: entries the server has caught up on are deleted from `pendingMap` as
 * they are found. That is deliberate — seeing the server report the new name IS
 * the confirmation signal, so detecting it and releasing the guard are one pass.
 * Releasing on a timer or on API success instead would reopen the race, because
 * Drive propagation can outlast several poll ticks.
 *
 * @param {Array<{id: any, name: string}>} items freshly fetched Drive items
 * @param {Map<string, string>} pendingMap driveItemId -> optimistically applied name
 * @returns {Array} a new array when anything is pending; untouched items keep their
 *   identity so downstream signature comparisons stay cheap
 */
export const applyPendingRenames = (items, pendingMap) => {
  if (!pendingMap || pendingMap.size === 0) return items || [];

  return (items || []).map((it) => {
    const key = String(it?.id ?? '');
    const pending = pendingMap.get(key);
    if (pending === undefined) return it;
    if (it?.name === pending) {
      // Drive has caught up. Drop the guard and take the server's word from here.
      pendingMap.delete(key);
      return it;
    }
    return { ...it, name: pending };
  });
};
